/**
 * 认证 Provider - 管理用户认证状态和流程
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useAppStore } from '../stores/useAppStore';
import { initTelegram } from '../telegram';

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  user: any;
  error: string | null;
  login: () => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState<string | null>(null);

  // 使用 ref 防止重复初始化
  const isInitializedRef = useRef(false);

  // 从 store 获取认证相关的方法
  const { telegramLogin, checkAuthStatus: storeCheckAuthStatus } = useAppStore();

  // 认证流程
  const login = useCallback(async () => {
    if (isAuthenticating) {
      console.log('⏳ 认证流程正在进行中，跳过重复认证');
      return;
    }

    try {
      setIsAuthenticating(true);
      setError(null);

      console.log('🔐 开始认证流程...');

      // 1. 检查是否有已保存的 token
      const savedToken = localStorage.getItem('authToken');
      if (savedToken) {
        console.log('🔍 发现已保存的 token，验证有效性...');
        const isValid = await storeCheckAuthStatus();
        if (isValid) {
          console.log('✅ Token 验证成功，用户已认证');
          setIsAuthenticated(true);
          return;
        } else {
          console.log('❌ Token 无效，清除并重新登录');
          localStorage.removeItem('authToken');
        }
      }

      // 2. 进行 Telegram WebApp 登录
      console.log('🔐 开始 Telegram WebApp 登录...');
      const telegramWebApp = (window as any).Telegram?.WebApp;

      if (!telegramWebApp?.initData) {
        throw new Error('无法获取 Telegram WebApp initData');
      }

      // 3. 调用后端登录接口
      console.log('📡 调用后端登录接口...');
      const loginResult = await telegramLogin(telegramWebApp.initData);

      if (!loginResult) {
        throw new Error('后端登录失败');
      }

      console.log('✅ Telegram WebApp 登录成功，后端认证完成');
      setIsAuthenticated(true);
    } catch (error) {
      console.error('❌ 认证流程失败:', error);
      setError(`认证失败: ${error instanceof Error ? error.message : '未知错误'}`);
      setIsAuthenticated(false);
    } finally {
      setIsAuthenticating(false);
    }
  }, [isAuthenticating, storeCheckAuthStatus, telegramLogin]); // 保持依赖，但添加防重复调用逻辑

  // 登出
  const logout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
  };

  // 检查认证状态
  const checkAuthStatus = useCallback(async (): Promise<boolean> => {
    try {
      const isValid = await storeCheckAuthStatus();
      setIsAuthenticated(isValid);
      return isValid;
    } catch (error) {
      console.error('❌ 检查认证状态失败:', error);
      setIsAuthenticated(false);
      return false;
    }
  }, [storeCheckAuthStatus]);

  // 初始化认证状态
  useEffect(() => {
    const initializeAuth = async () => {
      if (isInitializedRef.current) {
        console.log('⏳ 认证初始化已进行，跳过重复初始化');
        return;
      }

      isInitializedRef.current = true;
      console.log('🚀 初始化认证状态...');

      // 初始化 Telegram WebApp
      initTelegram();

      // 检查现有认证状态
      const savedToken = localStorage.getItem('authToken');
      const has401Error = sessionStorage.getItem('auth_401_error');

      if (savedToken && !has401Error) {
        // 有token且没有401错误，验证token
        const isValid = await checkAuthStatus();
        if (!isValid) {
          console.log('❌ Token 验证失败，清除token并重新登录');
          localStorage.removeItem('authToken');
          await login();
        }
      } else if (has401Error) {
        // 有401错误标志，跳过token验证
        console.log('🔐 检测到401错误标志，跳过token验证，直接进入重新授权流程');
        sessionStorage.removeItem('auth_401_error');
        // 触发重新认证流程
        await login();
      } else {
        // 没有token，直接进行认证
        console.log('🔐 没有保存的token，开始认证流程');
        await login();
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次，忽略依赖警告

  const value: AuthContextType = {
    isAuthenticated,
    isAuthenticating,
    user,
    error,
    login,
    logout,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook 用于使用认证上下文
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
